import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { readFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';

import type { watchContentHeaderTemplateDataContentFragment$data } from './__generated__/watchContentHeaderTemplateDataContentFragment.graphql.ts';

const contentHeaderTemplateDataFragment = graphql`
	fragment watchContentHeaderTemplateDataContentFragment on Content {
		metadata {
			sourceTemplateEntityId
		}
	}
`;

const CACHE_RETRY_INTERVAL_MS = 100;
const MAX_CACHE_READ_ATTEMPTS = 50;

export type ContentHeaderTemplateDataSubscription = {
	unsubscribe: () => void;
};

type TemplateDataResult =
	| { status: 'pending' }
	| { status: 'ready'; sourceTemplateEntityId: string | null };

const readTemplateData = (contentId: string): TemplateDataResult => {
	try {
		const data = readFragmentInterop<watchContentHeaderTemplateDataContentFragment$data>({
			id: `Content:${contentId}`,
			fragment: contentHeaderTemplateDataFragment,
		});

		const sourceTemplateEntityId = data?.metadata?.sourceTemplateEntityId;
		return sourceTemplateEntityId === undefined
			? { status: 'pending' }
			: { status: 'ready', sourceTemplateEntityId };
	} catch {
		return { status: 'pending' };
	}
};

/**
 * Reads creation-template provenance from the Content record already populated by the content
 * header. Reads remain cache-only; the bounded retry handles a late preload cache write without
 * executing another GraphQL operation.
 */
export const watchContentHeaderTemplateData = (
	contentId: string,
	onTemplateData: (sourceTemplateEntityId: string | null) => void,
): ContentHeaderTemplateDataSubscription => {
	let attempt = 0;
	let timeoutId: ReturnType<typeof setTimeout> | undefined;
	let unsubscribed = false;

	const read = () => {
		if (unsubscribed) {
			return;
		}

		attempt += 1;
		const result = readTemplateData(contentId);
		if (result.status === 'ready') {
			onTemplateData(result.sourceTemplateEntityId);
			return;
		}

		if (attempt < MAX_CACHE_READ_ATTEMPTS) {
			timeoutId = setTimeout(read, CACHE_RETRY_INTERVAL_MS);
		}
	};

	read();

	return {
		unsubscribe: () => {
			unsubscribed = true;
			if (timeoutId !== undefined) {
				clearTimeout(timeoutId);
			}
		},
	};
};
